import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil, switchMap, map, of } from 'rxjs';
import { NgApexchartsModule } from 'ng-apexcharts';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { DashboardService } from '../../../core/services/dashboard.service';
import { ProcessService } from '../../../core/services/process.service';
import { ProcessProgress, ProcessesProgressResponse } from '../../../core/models/dashboard.model';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-ui-processes-progress',
  standalone: true,
  imports: [CommonModule, NgApexchartsModule, LoadingSpinnerComponent],
  templateUrl: './ui-processes-progress.component.html'
})
export class UiProcessesProgressComponent implements OnInit, OnDestroy {
  public processesProgress: ProcessProgress[] = [];
  public loading = false;
  public error = false;
  public chartOptions: any;
  public hasData = false;

  private destroy$ = new Subject<void>();

  constructor(
    private dashboardService: DashboardService,
    private processService: ProcessService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadProcessesProgress();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadProcessesProgress(): void {
    if (!this.authService.isAuthenticated()) {
      this.error = true;
      return;
    }

    this.loading = true;
    this.error = false;

    this.dashboardService.getProcessesProgress()
      .pipe(
        switchMap((response) => this.enrichProgressFromProcesses(response)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.processesProgress = response.data;
            this.hasData = response.data.length > 0;

            if (this.hasData) {
              this.updateChartData(response.data);
            }

            this.loading = false;
          } else {
            this.hasData = false;
            this.error = true;
            this.loading = false;
          }
        },
        error: (error) => {
          this.hasData = false;
          this.handleApiError(error);
          this.loading = false;
        }
      });
  }

  /**
   * Completa total/progresso via GET /processes quando a API de dashboard ainda retorna total zerado.
   */
  private enrichProgressFromProcesses(response: ProcessesProgressResponse) {
    if (!response.success || !response.data?.length) {
      return of(response);
    }

    const needsEnrichment = response.data.some(
      (item) => item.total_questionnaires === 0 && item.completed_questionnaires > 0
    );

    if (!needsEnrichment) {
      return of({
        ...response,
        data: response.data.map((item) => this.normalizeProcessProgress(item))
      });
    }

    return this.processService.getProcesses({ per_page: 50 }).pipe(
      map((processesResponse) => {
        if (!processesResponse.success || !processesResponse.data) {
          return {
            ...response,
            data: response.data.map((item) => this.normalizeProcessProgress(item))
          };
        }

        const processesById = new Map(
          processesResponse.data.map((process) => [process.id, process])
        );

        return {
          ...response,
          data: response.data.map((item) => {
            const process = processesById.get(item.id);
            if (!process?.answers || process.answers.expected <= 0) {
              return this.normalizeProcessProgress(item);
            }

            return this.normalizeProcessProgress({
              ...item,
              total_questionnaires: process.answers.expected,
              completed_questionnaires: process.answers.received,
              progress_percentage: Math.round(
                (process.answers.received / process.answers.expected) * 10000
              ) / 100
            });
          })
        };
      })
    );
  }

  private normalizeProcessProgress(process: ProcessProgress): ProcessProgress {
    const total = process.total_questionnaires ?? 0;
    const completed = process.completed_questionnaires ?? 0;

    if (total > 0) {
      return {
        ...process,
        progress_percentage: Math.round((completed / total) * 10000) / 100
      };
    }

    return process;
  }

  private updateChartData(processesProgress: ProcessProgress[]): void {
    const data = processesProgress.map((process) => process.progress_percentage);
    const categories = processesProgress.map((process) => process.name);

    this.chartOptions = {
      series: [
        {
          name: 'Progresso',
          data
        }
      ],
      colors: ['#845adf'],
      chart: {
        type: 'bar',
        height: 335,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '55%',
          borderRadius: 4
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number) => `${val}%`,
        style: {
          fontSize: '11px',
          fontWeight: 600
        }
      },
      xaxis: {
        categories,
        max: 100,
        tickAmount: 5,
        labels: {
          formatter: (val: string) => `${val}%`
        }
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '12px'
          }
        }
      },
      tooltip: {
        custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
          const process = processesProgress[dataPointIndex];
          return `<div class="px-3 py-2">
            <strong>${process.name}</strong><br/>
            ${process.completed_questionnaires}/${process.total_questionnaires} respostas (${process.progress_percentage}%)
          </div>`;
        }
      }
    };
  }

  private handleApiError(error: { status?: number }): void {
    this.error = true;

    if (error.status === 401) {
      console.warn('Token não fornecido, inválido ou expirado');
    } else if (error.status === 500) {
      console.error('Erro interno do servidor');
    }
  }
}
